import React, {useCallback, useEffect, useMemo, useState} from 'react'
import { styled } from '@mui/material/styles';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {useDropzone} from 'react-dropzone'

import {findComponent} from "../../../../redux/page/pageActions";
import {appSetAlert, appSetModalContent} from "../../../../redux/app/appActions";

import axios from "axios";
import {axiosDelay} from "../../../../config/config";

// import ProgressBar from '../ProgressBar';

import useMainCanvasSize from "../../../../hooks/useMainCanvasSize";
import useWindowSize from "../../../../hooks/useWindowSize";
import useDataSource from "../../../../hooks/useDataSource";

import {getSecurityAccess} from "../../../../utilities/SecurityUtilities";

import Grid from "@mui/material/Grid";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import {useTheme} from "@mui/material";

import DropZoneLoaderTable from "./DropZoneLoaderTable";
import SettingsForm from "../settings/SettingsForm";

import Loader from "react-spinners/ClipLoader";

const PREFIX = 'FileDropZone';

const classes = {
    root: `${PREFIX}-root`,
    cardHeader: `${PREFIX}-cardHeader`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
		height: '100%'
	},

    [`& .${classes.cardHeader}`]: {
		padding: '8px 8px 0px 8px',
		alignItems: 'start',
		textAlign: 'left',
		textTransform: 'none',
	}
}));

const baseStyle = {
	flex: 1,
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	height: '100%',
	width: '100%',
	padding: '20px',
	borderWidth: 2,
	borderRadius: 2,
	borderColor: '#eeeeee',
	borderStyle: 'dashed',
	backgroundColor: '#fafafa',
	color: '#bdbdbd',
	outline: 'none',
	transition: 'border .24s ease-in-out'
};

const focusedStyle = {
	borderColor: '#2196f3'
};

const acceptStyle = {
	borderColor: '#00e676'
};

const rejectStyle = {
	borderColor: '#ff1744'
};


export default function FileDropZone({id, configuration, xId}) {
	const dispatch = useDispatch();
	const theme = useTheme();
	const { t } = useTranslation();

	// App State
	const config = useSelector(state => state.config);
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);
	const projectUserGroups = useSelector(state => state.user.projectUserGroups);
	const appUUID = useSelector(state => state.app.uuid);

	// Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title !== undefined ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle !== undefined ? compConfig.name.subtitle : '');
	// const [progressType, setProgressType] = useState(compConfig.element?.progressType !== undefined ? compConfig.element.progressType : "linear");
	const [type, setType] = useState(compConfig.element?.type !== undefined ? compConfig.element.type : 'put');
	const [acceptedFileTypes, setAcceptedFileTypes] = useState(compConfig.element?.acceptedFileTypes !== undefined ? compConfig.element.acceptedFileTypes : 'text')
	const [showLoadingTable, setShowLoadingTable] = useState(compConfig.element?.showLoadingTable !== undefined ? compConfig.element.showLoadingTable : false);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});
	const [resizedComponent, setResizedComponent] = useState(false);
	const [file, setFile] = useState(null);
	const [fileConfig, setFileConfig] = useState(null);
	const [fileLocation, setFileLocation] = useState(null);
	// const [progress, setProgress] = useState(0);
	const [fetching, setFetching] = useState(false);
	const [showProgressSpinner, setShowProgressSpinner] = useState(false);
	const [noProjectMessage, setNoProjectMessage] = useState(null);
	const [fileTypeMessage, setFileTypeMessage] = useState(null);
	const [uniqueID, setUniqueID] = useState(null);
	const [isDisabled, setIsDisabled] = useState(true);
	const [timeoutID, setTimeoutID] = useState(null);
	const [importID, setImportID] = useState(null);
	const [importTime, setImportTime] = useState(0);
	const [statusData, setStatusData] = useState(null);
	const [clickImport, setClickImport] = useState(false);

	const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource?.name : '';
	const styles = compConfig?.element?.styles;
	const location = compConfig.element?.location !== undefined ? compConfig.element.location : null;

	const dataSource = useDataSource(dataSourceName);
	const size = useMainCanvasSize();
	const windowSize = useWindowSize();

	// Load Status Data
	useEffect(() => {
		if (showLoadingTable) {
			if (uniqueID && fileConfig) {
				loadStatusData(clickImport);
			}
		}
	}, [showLoadingTable, fileConfig, uniqueID]);

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	// Resize Card
	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
				setResizedComponent(true);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Drop Zone Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'DropZone'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

	// Set Resized component
	useEffect( () => {
		// setTimeout required to accurately obtain chart.renderTo.clientHeight and chart.renderTo.clientWidth to perform the reflow
		setTimeout(function() {
			setResizedComponent(resizedComponent === true ? false : false);
		}, 300)
	}, [size, windowSize, resizedComponent, cardHeaderStyle, cardContentStyle])

	// Check External DataSource is filtered to ONE record
	useEffect(() => {
		// console.log('-----------------Drop Zone---------STATUS->');
		// console.log('id: {0}'.format(id));
		// console.log('fetching: {0}'.format(dataSource.fetching));
		// console.log('fetched: {0}'.format(dataSource.fetched));
		// console.log('error: {0}'.format(dataSource.error));
		// console.log('data:', dataSource.data);

		if (dataSource.error) {
			setFile(null);
			// setProgress(0);
			setNoProjectMessage("Error finding Project!");
			setIsDisabled(true);
		}
		else if (dataSource.data && dataSource.fetched) {

			if (dataSource.data.length > 1) {
				setFile(null);
				// setProgress(0);
				setNoProjectMessage("A single project must be selected!");
				setIsDisabled(true);
				setStatusData(null);
			} else {

				const projectID = dataSource.data[0]?.projectid !== undefined ? dataSource.data[0].projectid : undefined;
				const restricted = getSecurityAccess(projectID, projectUserGroups, configuration.security.restrict);

				if (restricted) {
					setIsDisabled(true);
				} else {
					setFile(null);
					setNoProjectMessage(null);
					setUniqueID(dataSource.data[0][compConfig.dataSource.field]);

					if (type.toLowerCase() === 'import')
					{
						setFileConfig(JSON.parse(dataSource.data[0][compConfig.dataSource.config]));
					} else if (type.toLowerCase() === 'put') {
						setFileConfig({source: {name: compConfig.element.name, type: acceptedFileTypes}});

						const pathValues = [];
						const valueFields = location.directory.valueFields;

						for (let i = 0; i < valueFields.length; i++ ) {

							// Check for path values needing to be set here
							if (valueFields[i].substring(0,5) === "$uuid") {
								pathValues.push(appUUID);
							} else {
								pathValues.push(dataSource.data[0][valueFields[i]]);
							}

						}

						const newLocation = JSON.parse(JSON.stringify(location));
						newLocation.directory.valueFields = pathValues;
						setFileLocation(newLocation);
					}

					setIsDisabled(false);
				}
			}
		}
	}, [dataSource.data, dataSource.fetching, dataSource.fetched, dataSource.error]);

	// Set UI message for Accepted File Types
	useEffect(() => {
		if (acceptedFileTypes) {
			const message = "Only the following file types will be accepted: " + getAcceptedFileTypes(acceptedFileTypes);
			setFileTypeMessage(message);
		}
	},[acceptedFileTypes]);

	const loadStatusData = (clickImport) => {

		const isClicked = clickImport;

		// No need for two spinners at a time
		// If showProgressSpinner is spinning, don't setFetching which shows linearprogress on Table
		if (!showProgressSpinner) {
			setFetching(true);
		}

		// Clear any existing timeout
		if (timeoutID !== null) {
			window.clearTimeout(timeoutID);
			setTimeoutID(null);
		}

		const url = config.data.dataLoader.apiDataLoader + "LoadingFileStatus";// + feature.options.featureOptions.target.name;
		const loaderType = fileConfig.source.name;
		const dbSource = config.data.authentication.apiSource;

		axios.get(url, {
			params: {
				loaderType: loaderType,
				uniqueID: uniqueID,
				db: dbSource
			}
		})
			.then(function (response) {

				// Parse out loading status i.e. we could be importing or publishing
				const data = response.data;

				// Sort most recent first
				data.sort(function(a,b){
					// Turn your strings into dates, and then subtract them
					// to get a value that is either negative, positive, or zero.
					return new Date(b.LastEditedOn) - new Date(a.LastEditedOn);
				});

				let inProgress = false;
				if (data.length > 0 && data[0].Status === 'In progress ...') {
					inProgress = true;
				}

				// Data going to the Status Table
				setStatusData(data);

				if (!showProgressSpinner) {
					setFetching(inProgress ? true : false);
				}

				// If we're loading then we'll want to refresh data throughout the cycle
				if (inProgress) {
					setImportID(data[0].ID);

					// Get local date and time but in UTC format to match Server UTC time
					let today = new Date();
					let date = today.getUTCFullYear()+'-'+(today.getUTCMonth()+1)+'-'+today.getUTCDate();
					let time = today.getUTCHours() + ":" + today.getUTCMinutes() + ":" + today.getUTCSeconds();
					let dateTime = date+' '+time;
					let newDate = new Date(dateTime);

					// Convert RunStartDateTime to DATE object
					const runStartDateTime = response.data[0].RunStartDateTime;
					let newRunStartDateTime = new Date(runStartDateTime);

					// Convert and Format to 00:00:00
					let measuredTime = new Date(null);

					measuredTime.setSeconds((newDate - newRunStartDateTime) / 1000); // specify value of SECONDS
					let MHSTime = measuredTime.toISOString().substr(11, 8);

					setImportTime(MHSTime);

					const newTimeoutID = setTimeout(function () {
						loadStatusData(isClicked);
					}, 2500);
					setTimeoutID(newTimeoutID)
				} else {
					if (isClicked) {
						// TODO Do we need a final check here?
						// appAlert('upload', statusData[0].Status);
					}
				}
			})
			.catch(function (error) {
				console.log('**ERROR**', error);
				setFetching(false);
			});
	};

	const uploadFile = (() => {
		// console.log(file);
		// console.log(uniqueID);
		// console.log(fileConfig);

		setShowProgressSpinner(true);
		setIsDisabled(true);	// Disable things while uploading
		setFetching(true);
		setClickImport(true);
		const isClicked = true;

		// Load Status Data while uploading file
		if (showLoadingTable) {
			const newTimeoutID = setTimeout(function () {
				loadStatusData(isClicked);
			}, 2500);
			setTimeoutID(newTimeoutID)
		}

		const axiosFileConfig = {
			// onUploadProgress: function(progressEvent) {
			// 	const percentCompleted = Math.round( (progressEvent.loaded * 100) / progressEvent.total );
			// 	setProgress(percentCompleted);
			// }
		};

		// https://stackoverflow.com/questions/70304877/how-to-send-both-text-and-binary-data-in-axios-post-request
		// Initializing form data and passing the file as a param
		const formData = new FormData();

		if (type.toLowerCase() === 'import') {

			formData.append('file', file);
			formData.append('source', JSON.stringify(fileConfig.source));
			formData.append('sourceSite', config.data.dataLoader.sourceSite);
			formData.append('targets', JSON.stringify(fileConfig.targets));
			formData.append('targetDB', config.data.authentication.apiSource);
			formData.append('uniqueID', uniqueID);

			setTimeout(function () {
				// File Loader
				const url = config.data.dataLoader.apiDataLoader + "loadfile";

				axios.post(url, formData, axiosFileConfig)
					.then(function (response) {
						// console.log(response);

						if (showLoadingTable) {
							loadStatusData(isClicked);
						}

						appAlert('upload', 'completed');

						// All Good --> nothing disabled
						acceptedFiles.length = 0;
						acceptedFiles.splice(0, acceptedFiles.length);
						setFile(null);
						setFetching(false);
						// setProgress(0);
						setShowProgressSpinner(false);
						setIsDisabled(false);
					})
					.catch(function (error) {
						// console.log('**ERROR**', error);

						if (showLoadingTable) {
							loadStatusData(isClicked);
						}

						appAlert('upload', 'error');

						// All Good --> nothing disabled
						acceptedFiles.length = 0;
						acceptedFiles.splice(0, acceptedFiles.length);
						setFile(null);
						setFetching(false);
						// setProgress(0);
						setShowProgressSpinner(false);
						setIsDisabled(false);
					});
			}, JSON.parse(axiosDelay()));

		} else if (type.toLowerCase() === 'put') {

			formData.append('file', file);
			formData.append('source', JSON.stringify(fileConfig.source));
			formData.append('sourceSite', config.data.dataLoader.sourceSite);
			formData.append('location', JSON.stringify(fileLocation));
			formData.append('fileStorage', config.data.dataLoader.fileStorage);
			formData.append('targetDB', config.data.authentication.apiSource);
			formData.append('uniqueID', uniqueID);

			setTimeout(function () {
				// File Loader
				const url = config.data.dataLoader.apiDataLoader + "loadfile";

				axios.put(url, formData, axiosFileConfig)
					.then(function (response) {
						// console.log(response);

						if (showLoadingTable) {
							loadStatusData(isClicked);
						}

						appAlert('upload', 'completed');

						// All Good --> nothing disabled
						acceptedFiles.length = 0;
						acceptedFiles.splice(0, acceptedFiles.length);
						setFile(null);
						setFetching(false);
						// setProgress(0);
						setShowProgressSpinner(false);
						setIsDisabled(false);
					})
					.catch(function (error) {
						// console.log('**ERROR**', error);

						if (showLoadingTable) {
							loadStatusData(isClicked);
						}

						appAlert('upload', 'error');

						// All Good --> nothing disabled
						acceptedFiles.length = 0;
						acceptedFiles.splice(0, acceptedFiles.length);
						setFile(null);
						setFetching(false);
						// setProgress(0);
						setShowProgressSpinner(false);
						setIsDisabled(false);
					});
			}, JSON.parse(axiosDelay()));

		}

	});

	const appAlert = (type, status) => {
		if (type.toLowerCase() === 'upload') {
			switch(status.toLowerCase()) {
				case 'completed':
					dispatch(appSetAlert('File Upload', 'File uploaded & imported successfully.', 'success', 4000));
					break;
				case 'error':
					dispatch(appSetAlert('File Upload Error', 'File failed to upload', 'error', 4000));
					break;
				default:
					break;
			}
		}
	}

	const onDropFile = useCallback(acceptedFiles => {
		// console.log(acceptedFiles);

		// Do something with the files
		acceptedFiles.map(f => {
			setFile(f);
		});
	}, []);

	const onDropRejection = useCallback(rejectedFiles => {
		// console.log(rejectedFiles);

		setFile(null);
		// setIsDisabled(true);

		// Do something with the files
		rejectedFiles.map(file => {
			// console.log(file);
			dispatch(appSetAlert('Upload Error', file.errors[0].message, 'error', 10000));
			// <li key={file.path}>
			// 	{file.path} - {file.size} bytes
			// </li>
		});
	}, []);

	const getAcceptedFileTypes = (mimeTypes) => {
		if (mimeTypes) {
			switch (mimeTypes.toLowerCase()) {
				case 'excel':
					return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,applicaiton/vnd.ms-excel"
				case 'csv':
					// https://christianwood.net/posts/csv-file-upload-validation/
					return "text/plain,text/x-csv,application/vnd.ms-excel,application/csv,application/x-csv,text/csv,text/comma-separated-values,text/x-comma-separated-values,text/tab-separated-values"
				case 'gif':
					return "image/gif"
				case 'ico':
					return "image/vnd.microsoft.icon"
				case 'jpeg':
				case 'jpg':
				case 'png':
					return "image/*"
				case 'svg':
					return "image/svg+xml"
				case 'tiff':
					return "image/tiff"
				case 'json':
					return "application/json"
				case 'pdf':
					return "application/pdf"
				case 'txt':
					return "text/plain"
				case 'cfg':
				case 'inp':
					return ""
				// default:
				// 	return "text/plain"
			}
		} else {
			return "text/plain"
		}
	}

	const {acceptedFiles, fileRejections, getRootProps, getInputProps, isFocused, isDragActive, isDragAccept, isDragReject} = useDropzone({
		maxFiles: 1,
		accept: getAcceptedFileTypes(acceptedFileTypes),
		onDrop: onDropFile,
		onDropRejected: onDropRejection
	});

	const acceptedFileItems = acceptedFiles.map(file => (
		<li key={file.path}>
			{file.path} - {file.size} bytes
		</li>
	));

	// const fileRejectionItems = fileRejections.map(({ file, errors }) => (
	// 	<li key={file.path}>
	// 		{file.path} - {file.size} bytes
	// 		<ul>
	// 			{errors.map(e => (
	// 				<li key={e.code}>{e.message}</li>
	// 			))}
	// 		</ul>
	// 	</li>
	// ));

	const style = useMemo(() => ({
		...baseStyle,
		...(isFocused ? focusedStyle : {}),
		...(isDragAccept ? acceptStyle : {}),
		...(isDragReject ? rejectStyle : {})
	}), [
		isFocused,
		isDragAccept,
		isDragReject
	]);

	const cssOverride = `
        display: block;
        margin-left: 0px;
        margin-top: 0px
    `;

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {...styles?.cardContent
			,height: 'calc(100% - 49px)'
			,overflow: 'auto'
			,padding: '0px'}
		);

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

	return (
        <Root className={classes.root}>
			<Card style={{height: '100%', boxShadow: 'none', ...styles?.card}}>
				<CardHeader className={classes.cardHeader} style={cardHeaderStyle}
							titleTypographyProps={{color: 'textPrimary', variant: 'subtitle2'}}
							title={t(title)}
							subheaderTypographyProps={{color: 'textSecondary', variant: 'body2'}}
							subheader={t(subtitle)}>
				</CardHeader>
				<CardContent style={cardContentStyle}>
					<div className="container" style={{width: '100%', height: '100%'}}>
						<Grid container style={{width: '100%', height: '200px', padding: '8px'}}>
							{/*<Grid item xs={12} style={{height: '40px', margin: '8px'}}>*/}
							{/*	<Grid container>*/}
							{/*		<Grid item xs={11}>*/}
							{/*			/!*<ProgressBar type={progressType} loadProgress={progress} />*!/*/}
							{/*		</Grid>*/}
							{/*		<Grid item xs={1} style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>*/}
							{/*			<Button type="submit" color={'primary'} size={'medium'} variant={'contained'}*/}
							{/*					disabled={!file || isDisabled} onClick={() =>{ uploadFile() }}>*/}
							{/*				Upload*/}
							{/*			</Button>*/}
							{/*		</Grid>*/}
							{/*	</Grid>*/}
							{/*</Grid>*/}
							<Grid item xs={10}>
								<div style={{height: '100%', width: '100%', position: "relative"}}>
									{isDisabled &&
										<div style={style}>
											{noProjectMessage}
										</div>
									}
									{!isDisabled &&
										<div {...getRootProps({style})}>
											<input {...getInputProps()} />
											{
												isDragActive && !isDragReject ?
													<div>Drop the file here ...</div> :
														!isDragActive && !isDragReject ?
														(
															<React.Fragment>
																<div>Drag 'n' drop a file here, or click to select files</div>
																<div style={{fontStyle: 'italic'}}>{fileTypeMessage}</div>
															</React.Fragment>
														) : <React.Fragment/>
											}
											{
												isDragReject ? <div>File Rejected </div> : <React.Fragment/>
											}
											{
												acceptedFiles ?
													(
														<ul>
															{acceptedFileItems}
														</ul>
													) : <React.Fragment/>

											}
											{/*{*/}
											{/*	fileRejections ?*/}
											{/*		(*/}
											{/*			<ul>*/}
											{/*				{fileRejectionItems}*/}
											{/*			</ul>*/}
											{/*		) : <React.Fragment/>*/}
											{/*}*/}
											{
												noProjectMessage ?
													<div>{noProjectMessage}</div> : <React.Fragment/>
											}
										</div>
									}
									{showProgressSpinner &&
										<div style={{top: 0, left: 0, marginTop: '1%', marginLeft: '44%', position: 'absolute'}}>
											<Loader
												css={cssOverride}
												sizeUnit={'px'}
												size={150}
												color={theme.palette.primary.main}
												loading={true}
											/>
										</div>
									}
								</div>
							</Grid>
							<Grid item xs={2} style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
								<Button type="submit" color={'primary'} size={'medium'} variant={'contained'}
										disabled={!file || isDisabled} onClick={() =>{ uploadFile() }}>
									Upload
								</Button>
							</Grid>
						</Grid>
						{showLoadingTable &&
							<Grid container style={{width: '100%', height: 'calc(100% - 200px', padding: '8px'}}>
								<Grid item xs={12} style={{height: '100%'}}>
									<DropZoneLoaderTable loading={showProgressSpinner ? false : fetching} data={statusData} importID={importID} importTime={importTime}/>
								</Grid>
							</Grid>
						}
					</div>
				</CardContent>
			</Card>
		</Root>
    );
}
